import { c as _c } from "react/compiler-runtime";
import clsx from "classnames";
import { NodeKey } from "lexical";
import { GridBlock, GridSlot, GridSlotSize } from "common/types/graphql";
import { FrontendBlock, ImageAspectRatio } from "modules/editor/types";
import { ContentSummaryVariant } from "common/components/ContentSummary";
import { BlockComponent } from "../BlocksRenderer";
import { HIDE_FIRST_ROW_DIVIDERS_CLASSES, ImmutableGridSlot } from "./ImmutableGridSlot";
import { MutableGridSlot } from "./MutableGridSlot";
import { imageSizes } from "common/lib/imageSizes";
import { usePageProps } from "common/hooks/data/usePageProps";
import type { EditPageProps } from "pages/edit/index.page";
import { ContentSummaryFrontendBlock } from "modules/editor/blocks/ContentSummary";
import type { DragItem } from "./types";
import { useCallback, useState } from "react";
export const INDEX_FOR_NO_GRID_SLOT = -1;
const slotSizeToContentSummaryPropsMap: Record<GridSlotSize, Partial<ContentSummaryFrontendBlock>> = {
  [GridSlotSize.Base]: {
    imageSizes: imageSizes({
      md: 0.25,
      sm: 0.5
    }),
    variant: ContentSummaryVariant.Base
  },
  [GridSlotSize.BaseWide]: {
    imageSizes: imageSizes({
      lg: 0.125,
      sm: 0.25
    }),
    variant: ContentSummaryVariant.BaseWide
  },
  [GridSlotSize.Lg]: {
    imageSizes: imageSizes({
      lg: 0.5,
      md: 1
    }),
    desktopImageAspectRatio: ImageAspectRatio.Square,
    variant: ContentSummaryVariant.Hero
  },
  [GridSlotSize.Xl]: {
    imageSizes: imageSizes({
      lg: 0.5,
      sm: 1
    }),
    desktopImageAspectRatio: ImageAspectRatio.Landscape,
    variant: ContentSummaryVariant.Hero
  },
  [GridSlotSize.Xxl]: {
    imageSizes: imageSizes(),
    desktopImageAspectRatio: ImageAspectRatio.Ultrawide,
    variant: ContentSummaryVariant.Hero
  },
  [GridSlotSize.NearFullByFour]: {
    imageSizes: imageSizes({
      lg: 0.75,
      md: 1
    }),
    desktopImageAspectRatio: ImageAspectRatio.Square,
    desktopImageCrop: ImageAspectRatio.Landscape,
    variant: ContentSummaryVariant.Hero
  },
  [GridSlotSize.NearFullByThree]: {
    imageSizes: imageSizes({
      lg: 0.75,
      md: 1
    }),
    desktopImageAspectRatio: ImageAspectRatio.Square,
    desktopImageCrop: ImageAspectRatio.Landscape,
    variant: ContentSummaryVariant.Hero,
    // Text only variants that pair with this don't need that much height
    className: "lg:min-h-fit xl:min-h-fit"
  },
  [GridSlotSize.NearFullByTwo]: {
    imageSizes: imageSizes({
      lg: 0.75,
      md: 1
    }),
    desktopImageAspectRatio: ImageAspectRatio.Square,
    desktopImageCrop: ImageAspectRatio.Landscape,
    variant: ContentSummaryVariant.Hero
  },
  [GridSlotSize.DisappearingThumbnail]: {
    imageSizes: imageSizes({
      lg: 0.2,
      sm: 0.25
    }),
    variant: ContentSummaryVariant.DisappearingThumbnail
  }
};

/**
 * Configuration for blocks based on the slot size.
 *
 * This function adds additional props to the blocks based on the slot size the slot size they are positioned.
 * These additional props can define what variant, image aspect ratio and image sizes the block should use.
 *
 * The extra props will only be passed to the block if the block is a ContentSummary or CustomURL block, otherwise
 * the block will be returned as is.
 */
const getAdjustedBlocksConfig = (blocks: FrontendBlock[], slotSizes: GridSlotSize[]): FrontendBlock[] => slotSizes.map((slotSize, index: number) => {
  const block = blocks[index] || null;
  if (block?.type === "CONTENT_SUMMARY" || block?.type === "CUSTOM_URL") {
    return {
      ...block,
      ...slotSizeToContentSummaryPropsMap[slotSize]
    };
  }
  return block;
});
export interface GridProps extends Omit<GridBlock, "blocks" | "columns"> {
  blocks: FrontendBlock[];
  slotSizes: GridSlotSize[];
  slotTypes: GridSlot[];
  isFirstPageGrid?: boolean;
  nodeKey?: NodeKey;
  onGridSlotClear?: (index: number) => void;
  onGridSlotDrop?: (index: number, item: DragItem) => void;
  onGridSlotSwap?: (a: number, b: number, item: DragItem) => void;
  onGridSlotEdit?: (index: number) => void;
  onGridSlotAdd?: (index: number) => void;
}
export const Grid = t0 => {
  "use memo";

  const $ = _c(11);
  const {
    blocks: t1,
    slotSizes: t2,
    slotTypes: t3,
    isFirstPageGrid: t4,
    nodeKey,
    onGridSlotClear,
    onGridSlotDrop,
    onGridSlotSwap,
    onGridSlotEdit,
    onGridSlotAdd
  } = t0;
  const blocks = t1 === undefined ? [] : t1;
  const slotSizes = t2 === undefined ? [] : t2;
  let t5;
  if ($[0] !== t3) {
    t5 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const slotTypes = t5;
  const isFirstPageGrid = t4 === undefined ? false : t4;
  const {
    editing
  } = usePageProps();
  const [dragOver, setDragOver] = useState(INDEX_FOR_NO_GRID_SLOT);
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = index => {
      setDragOver(index);
    };
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const handleDraggingOver = t6;
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setDragOver(INDEX_FOR_NO_GRID_SLOT);
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const handleDragLeave = t7;
  const t8 = editing || blocks?.length > 0;
  let t9;
  if ($[4] !== isFirstPageGrid || $[5] !== t8) {
    t9 = clsx("grid grid-cols-3 gap-4 sm:grid-cols-12 sm:gap-6 sm:gap-y-2 xl:gap-x-8 xl:gap-y-4", {
      "mb-4": t8,
      [HIDE_FIRST_ROW_DIVIDERS_CLASSES]: isFirstPageGrid
    });
    $[4] = isFirstPageGrid;
    $[5] = t8;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const t10 = "grid";
  const t11 = getAdjustedBlocksConfig(blocks, slotSizes).map((block, index_0) => {
    const slotType = slotTypes?.[index_0] || GridSlot.Editorial;
    const slotSize = slotSizes[index_0];
    if (editing) {
      return block === null ? <MutableGridSlot key={index_0} gridNodeKey={nodeKey} index={index_0} size={slotSize} type={slotType} onDrop={onGridSlotDrop} onSwap={onGridSlotSwap} onAdd={onGridSlotAdd} onDraggingOver={handleDraggingOver} dragOver={dragOver === index_0} asPlaceholder={true} /> : <MutableGridSlot key={index_0} gridNodeKey={nodeKey} index={index_0} size={slotSize} type={slotType} onClear={onGridSlotClear} onDrop={onGridSlotDrop} onSwap={onGridSlotSwap} onEdit={onGridSlotEdit} onDraggingOver={handleDraggingOver} dragOver={dragOver === index_0}><BlockComponent block={block} /></MutableGridSlot>;
    }
    return block === null ? null : <ImmutableGridSlot key={index_0} size={slotSize} type={slotType}><BlockComponent block={block} /></ImmutableGridSlot>;
  });
  let t12;
  if ($[7] !== handleDragLeave || $[8] !== t11 || $[9] !== t9) {
    t12 = <section className={t9} onDragLeave={handleDragLeave} data-testid={t10}>{t11}</section>;
    $[7] = handleDragLeave;
    $[8] = t11;
    $[9] = t9;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  return t12;
};